<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Legajo documental</h1>
    </div>

    <wz-card>
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/3 mb-2 px-2">
          <label class="wz-form-label">Empresa</label>
          <wz-select v-model="filters.companyId" :items="companyOptions" defaultOption="Todas" @change="load" />
        </div>
        <div class="w-full md:w-1/3 mb-2 px-2">
          <label class="wz-form-label">Área</label>
          <wz-select v-model="filters.areaId" :items="areaOptions" defaultOption="Todas" @change="load" />
        </div>
        <div class="w-full md:w-1/3 mb-2 px-2">
          <label class="wz-form-label">Cargo</label>
          <wz-select v-model="filters.positionId" :items="positionOptions" defaultOption="Todos" @change="load" />
        </div>
      </div>
    </wz-card>

    <wz-card :title="`Legajos que no se pudieron generar (${failures.length})`" class="mt-3">
      <p class="text-muted text-sm mb-2">
        El contrato existe, pero no se creó ningún documento. Es una falla del sistema, no de la persona, así que
        <strong>no bloquea sus vacaciones ni sus movimientos, pero solo por unos días:</strong> al vencer el plazo, el
        documento cuenta como faltante. Reintentar no reinicia el plazo. "En cola" significa que se está generando en
        segundo plano. El más viejo va primero.
      </p>
      <wz-table v-if="failures.length">
        <thead>
          <tr><th>Colaborador</th><th>Empresa</th><th>Contrato</th><th>Estado</th><th>Días</th><th>Motivo</th><th>Intentos</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="f in failures" :key="f.contractId">
            <td><router-link :to="{ name: 'legajo-employee', params: { id: f.employeeId } }">{{ f.employeeName }}</router-link></td>
            <td>{{ f.companyName || '—' }}</td>
            <td>{{ f.contractNumber }}</td>
            <td><wz-badge :variant="failureVariant(f)" pill>{{ failureLabel(f) }}</wz-badge></td>
            <td>
              <strong>{{ f.daysOpen }}</strong>
              <div class="text-sm text-muted">{{ f.graceDaysLeft > 0 ? `quedan ${f.graceDaysLeft}` : 'plazo vencido' }}</div>
            </td>
            <td class="text-sm">{{ f.reason }}</td>
            <td>{{ f.attempts }}</td>
            <td><wz-button variant="primary" size="sm" @click="retry(f)">Reintentar</wz-button></td>
          </tr>
        </tbody>
      </wz-table>
      <p v-else class="mb-0 text-muted">Nada por aquí.</p>
    </wz-card>

    <wz-card :title="`Lotes de firma sin terminar (${signatureBatches.length})`" class="mt-3">
      <p class="text-muted text-sm mb-2">Reintenta el firmante desde su bandeja: es su autorización.</p>
      <wz-table v-if="signatureBatches.length">
        <thead>
          <tr><th>Firmante</th><th>Autorizado</th><th>En cola</th><th>Con error</th></tr>
        </thead>
        <tbody>
          <tr v-for="b in signatureBatches" :key="b.authorizationId">
            <td>{{ b.signerName }}</td>
            <td>{{ formatDate(b.authorizedAt) }} {{ String(b.authorizedAt || '').substring(11, 16) }}</td>
            <td>{{ b.queued }}</td>
            <td><wz-badge v-if="b.failed" variant="danger" pill>{{ b.failed }}</wz-badge><span v-else>0</span></td>
          </tr>
        </tbody>
      </wz-table>
      <p v-else class="mb-0 text-muted">Nada por aquí.</p>
    </wz-card>

    <wz-card :title="`Contratos renovados sin documento (${renewalGaps.length})`" class="mt-3">
      <p class="text-muted text-sm mb-2">
        Se renovaron y no hay documento para el período nuevo. Un contrato sin documento escrito es la infracción más
        cara: genera el documento de renovación desde el expediente.
      </p>
      <wz-alert variant="warning" class="mb-2">
        <strong>Esta lista es un piso, no el total.</strong> Las renovaciones hechas sobre el mismo contrato antes de
        que existiera este aviso no guardaron su fecha y no aparecen aquí: puede haber más contratos renovados sin
        documento de los que se ven.
      </wz-alert>
      <wz-table v-if="renewalGaps.length">
        <thead>
          <tr><th>Colaborador</th><th>Empresa</th><th>Contrato</th><th>Renovado el</th><th>Hasta</th></tr>
        </thead>
        <tbody>
          <tr v-for="g in renewalGaps" :key="g.contractId">
            <td><router-link :to="{ name: 'legajo-employee', params: { id: g.employeeId } }">{{ g.employeeName }}</router-link></td>
            <td>{{ g.companyName || '—' }}</td>
            <td>{{ g.contractNumber }}</td>
            <td>{{ formatDate(g.renewedAt) }}</td>
            <td>{{ formatDate(g.finishDate) }}</td>
          </tr>
        </tbody>
      </wz-table>
      <p v-else class="mb-0 text-muted">Nada por aquí.</p>
    </wz-card>

    <wz-card v-for="s in sections" :key="s.key" :title="`${s.title} (${s.rows.length})`" class="mt-3">
      <p class="text-muted text-sm mb-2">{{ s.help }}</p>
      <wz-table v-if="s.rows.length">
        <thead>
          <tr>
            <th>Colaborador</th>
            <th>Empresa</th>
            <th>Documento</th>
            <th>Estado</th>
            <th v-if="s.key === 'sentNotSigned'">Días desde el envío</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in s.rows" :key="d.id">
            <td><router-link :to="{ name: 'legajo-employee', params: { id: d.employeeId } }">{{ d.employeeName }}</router-link></td>
            <td>{{ d.companyName || '—' }}</td>
            <td>{{ d.documentTypeName }}</td>
            <td>{{ d.documentStatus }}<span v-if="d.deliveryMode"> · {{ d.deliveryMode }}</span></td>
            <td v-if="s.key === 'sentNotSigned'">{{ d.daysSinceSent }}</td>
          </tr>
        </tbody>
      </wz-table>
      <p v-else class="mb-0 text-muted">Nada por aquí.</p>
    </wz-card>

    <wz-card :title="`Sin consentimiento electrónico (${withoutConsent.length})`" class="mt-3">
      <p class="text-muted text-sm mb-2">A quiénes hay que regularizar. Sin consentimiento, todos sus documentos salen en físico.</p>
      <wz-table v-if="withoutConsent.length">
        <thead><tr><th>Colaborador</th><th>Empresa</th></tr></thead>
        <tbody>
          <tr v-for="e in withoutConsent" :key="e.employeeId + '-' + e.companyId">
            <td><router-link :to="{ name: 'legajo-employee', params: { id: e.employeeId } }">{{ e.employeeName }}</router-link></td>
            <td>{{ e.companyName }}</td>
          </tr>
        </tbody>
      </wz-table>
      <p v-else class="mb-0 text-muted">Nada por aquí.</p>
    </wz-card>

    <wz-card :title="`Sin correo para firmar (${withoutSigningChannel.length})`" class="mt-3">
      <p class="text-muted text-sm mb-2">Tienen documentos por firmar y su correo no sirve para firmar. Mientras no se resuelva, no reciben el código.</p>
      <wz-table v-if="withoutSigningChannel.length">
        <thead><tr><th>Colaborador</th><th>Problema</th></tr></thead>
        <tbody>
          <tr v-for="e in withoutSigningChannel" :key="e.employeeId">
            <td><router-link :to="{ name: 'legajo-employee', params: { id: e.employeeId } }">{{ e.employeeName }}</router-link></td>
            <td class="text-sm">{{ e.problem }}</td>
          </tr>
        </tbody>
      </wz-table>
      <p v-else class="mb-0 text-muted">Nada por aquí.</p>
    </wz-card>

    <wz-card :title="`Solicitudes de corrección del portal (${corrections.length})`" class="mt-3">
      <p class="text-muted text-sm mb-2">El colaborador no edita sus datos: los pide aquí. Corrígelos en su ficha y marca la solicitud como resuelta.</p>
      <wz-table v-if="corrections.length">
        <thead><tr><th>Colaborador</th><th>Dato</th><th>Valor pedido</th><th>Fecha</th><th></th></tr></thead>
        <tbody>
          <tr v-for="c in corrections" :key="c.id">
            <td><router-link :to="{ name: 'legajo-employee', params: { id: c.employeeId } }">{{ c.employeeName }}</router-link></td>
            <td>{{ c.fieldName }}</td>
            <td>{{ c.requestedValue }}</td>
            <td>{{ c.createdAt }}</td>
            <td><wz-button variant="light" @click="resolveCorrection(c)">Marcar resuelta</wz-button></td>
          </tr>
        </tbody>
      </wz-table>
      <p v-else class="mb-0 text-muted">Nada por aquí.</p>
    </wz-card>

    <wz-card title="Avisar documentos por selección" class="mt-3">
      <p class="text-muted text-sm mb-2">
        Usa los filtros de arriba, revisa a quiénes se avisaría y confirma. El aviso lleva el enlace al portal, nunca el documento.
        Solo salen documentos generados y sin avisar de quienes aceptaron la entrega electrónica.
      </p>
      <div class="flex justify-end mb-2">
        <wz-button variant="light" @click="loadSendPreview">Ver a quiénes se avisaría</wz-button>
      </div>
      <template v-if="sendPreview">
        <p class="mb-2">
          <strong>{{ selectedRecipients.length }}</strong> de {{ sendPreview.recipients.length }} persona(s) seleccionada(s).
          Sin consentimiento, no se avisan: <strong>{{ sendPreview.withoutConsent.length }}</strong>.
          Documentos que exigen entrega física: <strong>{{ sendPreview.physicalDocuments }}</strong>.
          <span v-if="sendPreview.waitingForCompany"><strong>{{ sendPreview.waitingForCompany }}</strong> documento(s) esperan la firma de la empresa: se avisan después.</span>
        </p>
        <wz-table v-if="sendPreview.recipients.length">
          <thead>
            <tr>
              <th><input type="checkbox" :checked="allRecipientsSelected" aria-label="Seleccionar todas" @change="toggleAllRecipients($event.target.checked)"></th>
              <th>Colaborador</th><th>Empresa</th><th>Documentos</th><th>Canales</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in sendPreview.recipients" :key="recipientKey(r)">
              <td><input v-model="chosenRecipients" type="checkbox" :value="recipientKey(r)" :disabled="!r.channels.length" :aria-label="r.employeeName"></td>
              <td><router-link :to="{ name: 'legajo-employee', params: { id: r.employeeId } }">{{ r.employeeName }}</router-link></td>
              <td>{{ r.companyName }}</td>
              <td>{{ r.documentNames.join(', ') }}</td>
              <td>
                <span v-if="r.channels.length">{{ r.channels.join(', ') }}</span>
                <wz-badge v-else variant="danger" pill>Sin correo</wz-badge>
              </td>
            </tr>
          </tbody>
        </wz-table>
        <p v-else class="mb-0 text-muted">Con estos filtros no hay nada por avisar.</p>
        <div v-if="sendPreview.recipients.length" class="flex justify-end mt-2">
          <wz-button variant="primary" :disabled="!selectedRecipients.length" @click="confirmSend">Avisar a {{ selectedRecipients.length }} persona(s)</wz-button>
        </div>
      </template>
    </wz-card>

    <wz-card title="Regularizar al personal actual" class="mt-3">
      <p class="text-muted text-sm mb-2">
        Crea una <strong>ficha pendiente</strong> por cada documento que le corresponde a cada colaborador vigente, para
        saber qué falta. <strong>No genera ningún documento:</strong> generar hoy el contrato de quien entró hace dos años
        fabricaría un documento que nunca existió. De cada ficha, sube el escaneo si existe en papel, o entrégalo de verdad
        hoy.
      </p>
      <div class="flex justify-end mb-2">
        <wz-button variant="light" @click="loadBulkPreview">Ver vista previa{{ filters.companyId ? ' de esta empresa' : ' de todas las empresas' }}</wz-button>
      </div>
      <template v-if="bulk">
        <p class="mb-2">
          <strong>{{ bulk.contracts }}</strong> contrato(s) vigente(s): se crearían <strong>{{ bulk.totalToCreate }}</strong>
          ficha(s) y se omiten <strong>{{ bulk.totalOmitted }}</strong>.
        </p>
        <wz-table>
          <thead>
            <tr>
              <th>Empresa</th>
              <th>Documento</th>
              <th>Se crean</th>
              <th title="Personal actual: su contrato ya existe, en papel o en el sistema anterior">Omitidas: contrato de trabajo</th>
              <th>Omitidas: ya tiene ficha</th>
              <th>Omitidas: ya entregado</th>
              <th>Omitidas: no es de ingreso</th>
              <th title="No impide crear la ficha: faltará para generar el documento">Sin plantilla</th>
              <th title="No impide crear la ficha: faltará para generar el documento">Sin firmante</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!bulk.rows.length">
              <td colspan="9" class="text-center text-muted py-4">No hay documentos obligatorios configurados para estos contratos.</td>
            </tr>
            <tr v-for="r in bulk.rows" :key="r.companyId + '-' + r.documentTypeId">
              <td>{{ r.companyName }}</td>
              <td>{{ r.documentTypeName }}</td>
              <td><strong>{{ r.toCreate }}</strong></td>
              <td>{{ r.omittedEmploymentContract }}</td>
              <td>{{ r.omittedExisting }}</td>
              <td>{{ r.omittedDelivered }}</td>
              <td>{{ r.omittedNotAtHiring }}</td>
              <td>{{ r.withoutTemplate }}</td>
              <td>{{ r.withoutSigner }}</td>
            </tr>
          </tbody>
        </wz-table>
        <div class="flex justify-end mt-2">
          <wz-button variant="primary" :disabled="!bulk.totalToCreate" @click="confirmBulk">Crear {{ bulk.totalToCreate }} ficha(s) pendiente(s)</wz-button>
        </div>
      </template>
    </wz-card>

    <wz-card title="Exportar expedientes" class="mt-3">
      <p class="text-muted text-sm mb-2">
        Un ZIP con los documentos de cada colaborador y un PDF índice con la bitácora de cada documento.
      </p>
      <div style="max-height: 240px; overflow-y: auto;" class="mb-2">
        <div v-for="e in people" :key="e.employeeId" class="mb-1">
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" v-model="selected" :value="e.employeeId" />
            <span>{{ e.employeeName }}</span>
          </label>
        </div>
        <p v-if="!people.length" class="mb-0 text-muted">Sin colaboradores con estos filtros.</p>
      </div>
      <div class="flex justify-end gap-2">
        <wz-button variant="light" :disabled="!people.length" @click="selected = people.map(p => p.employeeId)">Seleccionar todos</wz-button>
        <wz-button variant="primary" class="ml-2" :disabled="!selected.length || exporting" @click="exportSelected">
          {{ exporting ? 'Exportando...' : `Exportar (${selected.length})` }}
        </wz-button>
      </div>
    </wz-card>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import fileDownload from 'js-file-download'
import { LegajoService, CompanyService, AreaService, PositionService } from '@/core/services'

export default {
  name: 'LegajoDashboard',
  data() {
    return {
      filters: { companyId: null, areaId: null, positionId: null },
      corrections: [],
      sendPreview: null,
      chosenRecipients: [],
      dash: {},
      companies: [],
      areas: [],
      positions: [],
      selected: [],
      exporting: false,
      bulk: null,
      bulkCompanyId: null,
    }
  },
  computed: {
    selectedRecipients() {
      if (!this.sendPreview) return []
      return this.sendPreview.recipients.filter(r => this.chosenRecipients.includes(this.recipientKey(r)))
    },
    allRecipientsSelected() {
      const reachable = this.sendPreview ? this.sendPreview.recipients.filter(r => r.channels.length) : []
      return reachable.length > 0 && this.selectedRecipients.length === reachable.length
    },
    companyOptions() { return this.companies.map(c => ({ key: c.id, label: c.name })) },
    areaOptions() { return this.areas.map(a => ({ key: a.id, label: a.description })) },
    positionOptions() { return this.positions.map(p => ({ key: p.id, label: p.description || p.name })) },
    withoutConsent() { return this.dash.withoutConsent || [] },
    withoutSigningChannel() { return this.dash.withoutSigningChannel || [] },
    failures() { return this.dash.generationFailures || [] },
    signatureBatches() { return this.dash.signatureBatches || [] },
    renewalGaps() { return this.dash.renewalGaps || [] },
    sections() {
      const d = this.dash
      return [
        { key: 'sendFailures', title: 'Fallos de envío', help: 'Algún canal falló: correo rebotado, número inválido o sin datos de contacto. Revisa la bitácora del documento.', rows: d.sendFailures || [] },
        { key: 'altered', title: 'Archivos alterados', help: 'El archivo ya no coincide con el hash guardado al generarlo.', rows: d.altered || [] },
        { key: 'pending', title: 'Documentos que faltan', help: 'Pendientes de carga por el colaborador o de registro por RRHH.', rows: d.pending || [] },
        { key: 'sentNotOpened', title: 'Enviados sin abrir', help: 'El colaborador no se enteró.', rows: d.sentNotOpened || [] },
        { key: 'sentNotSigned', title: 'Entregados o enviados, sin firma registrada', help: 'Exigen la firma del colaborador y no tienen firma registrada. Si el archivo ya está firmado, usa «Registrar firma en papel» en el legajo.', rows: d.sentNotSigned || [] },
        { key: 'physicalOrNotarial', title: 'Pendientes físicos y notariales', help: 'Los que requieren presencia.', rows: d.physicalOrNotarial || [] },
      ]
    },
    // Colaboradores que se ven con estos filtros, para elegir a quién exportar.
    people() {
      const seen = new Map()
      this.sections.forEach(s => s.rows.forEach(r => seen.set(r.employeeId, r.employeeName)))
      this.withoutConsent.forEach(e => seen.set(e.employeeId, e.employeeName))
      this.withoutSigningChannel.forEach(e => seen.set(e.employeeId, e.employeeName))
      this.failures.forEach(e => seen.set(e.employeeId, e.employeeName))
      this.renewalGaps.forEach(e => seen.set(e.employeeId, e.employeeName))
      return [...seen.entries()].map(([employeeId, employeeName]) => ({ employeeId, employeeName }))
        .sort((a, b) => a.employeeName.localeCompare(b.employeeName))
    },
  },
  async created() {
    try {
      const [comp, area, pos] = await Promise.all([CompanyService.listAll(), AreaService.listActive(), PositionService.listAll()])
      this.companies = comp.payload || []
      this.areas = area.payload || []
      this.positions = pos.payload || []
    } catch (e) {
      console.error('Error cargando catálogos', e)
    }
    this.load()
  },
  methods: {
    async load() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = {}
        Object.entries(this.filters).forEach(([k, v]) => { if (v) params[k] = v })
        this.dash = (await LegajoService.dashboard(params)).payload || {}
        this.corrections = (await LegajoService.corrections()).payload || []
        this.selected = []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el tablero')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async loadBulkPreview() {
      try {
        await this.$store.dispatch('app/loading', true)
        this.bulkCompanyId = this.filters.companyId || null
        this.bulk = (await LegajoService.bulkPreview(this.bulkCompanyId)).payload
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al calcular la vista previa')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    confirmBulk() {
      Alert.dialog('Crear fichas pendientes',
        `Se crearán ${this.bulk.totalToCreate} ficha(s) y se omiten ${this.bulk.totalOmitted}. No se genera ningún documento. ¿Continuar?`,
        async () => {
          try {
            await LegajoService.bulkCreate(this.bulkCompanyId, this.bulk.token)
            Alert.success('Fichas pendientes creadas')
            this.bulk = null
            this.load()
          } catch (e) {
            Alert.error(e.errors ? e.errors.message : 'Error al crear las fichas')
          }
        })
    },
    failureLabel(f) {
      if (f.state === 'PENDIENTE') return 'Ya cuenta como faltante'
      return f.state === 'ERROR' ? 'Error' : 'En cola'
    },
    failureVariant(f) {
      if (f.state === 'PENDIENTE' || f.graceDaysLeft <= 2) return 'danger'
      return f.state === 'ERROR' ? 'warning' : 'secondary'
    },
    recipientKey(r) {
      return r.employeeId + '-' + r.companyId
    },
    toggleAllRecipients(checked) {
      this.chosenRecipients = checked ? this.sendPreview.recipients.filter(r => r.channels.length).map(this.recipientKey) : []
    },
    async loadSendPreview() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = {}
        Object.entries(this.filters).forEach(([k, v]) => { if (v) params[k] = v })
        this.sendPreview = (await LegajoService.sendPreview(params)).payload
        this.toggleAllRecipients(true)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al calcular a quiénes se avisaría')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    confirmSend() {
      const people = this.selectedRecipients
      Alert.dialog('Avisar documentos', `Se avisará a ${people.length} persona(s) de la lista. ¿Continuar?`, async () => {
        try {
          const ids = people.reduce((all, r) => all.concat(r.documentIds), [])
          await LegajoService.sendSelection(ids)
          Alert.success('Avisos en cola: salen en segundo plano. Los fallos aparecerán en este tablero.')
          this.sendPreview = null
        } catch (e) {
          Alert.error(e.errors ? e.errors.message : 'Error al enviar los avisos')
        }
      })
    },
    async resolveCorrection(c) {
      try {
        await LegajoService.resolveCorrection(c.id)
        this.corrections = this.corrections.filter(x => x.id !== c.id)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al resolver la solicitud')
      }
    },
    async retry(f) {
      try {
        await this.$store.dispatch('app/loading', true)
        await LegajoService.generate(f.contractId)
        Alert.success('En cola: se genera en segundo plano y desaparece de esta lista al terminar')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Volvió a fallar')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
      this.load()
    },
    formatDate(d) {
      if (!d) return '—'
      const parts = String(d).substring(0, 10).split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d
    },
    async exportSelected() {
      try {
        this.exporting = true
        const resp = await LegajoService.exportFile(this.selected)
        fileDownload(resp.data, 'expediente.zip')
      } catch (e) {
        Alert.error('Error al exportar el expediente')
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>
